// src/pages/theme/components/FontSelection.tsx
import * as React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
  createStyles,
} from "@material-ui/core";
import { CustomTheme } from "../../../../../types/customTheme";
import { AVAILABLE_FONTS } from "../../../../../utils/consts";

// Standard CSS font weights. Empty keeps the theme's default weight.
const FONT_WEIGHTS = ["100", "200", "300", "400", "500", "600", "700", "800", "900"];

// The extra size is a whole number of pixels, optionally negative.
const isValidFontSize = (size?: string) => /^-?\d+$/.test(String(size || ""));

// Keeps only digits and one leading minus, so nothing that would break the
// "Font*weight+size" value can be typed.
const cleanFontSize = (size: string) => {
  const isNegative = size.trim().startsWith("-");
  const digits = size.replace(/\D/g, "");
  return isNegative ? `-${digits}` : digits;
};

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    wrapper: {
      marginBottom: 16,
    },
    label: {
      margin: "4px 4px 4px 0",
      display: "flex",
      justifyContent: "start",
    },
    formControl: {
      width: "100%",
      borderRadius: "10px",
      backgroundColor: theme.palette.background.entity_highlight_background,
      [`& fieldset`]: {
        borderRadius: "10px",
        border: "none",
      },
    },
    previewBox: {
      marginTop: 8,
      padding: "10px 12px",
      borderRadius: "8px",
      border: `1px solid ${theme.palette.background.entity_border}`,
      minHeight: 28,
      overflow: "hidden",
    },
  }),
);

export interface FontSelectionProps {
  title: string;
  value: string;
  setValue: (value: string) => void;
  previewText?: string;
  fontWeight?: string;
  setFontWeight?: (value: string) => void;
  fontSize?: string;
  setFontSize?: (value: string) => void;
}

/* Dropdown for choosing a font, with a live text preview using the selected font. */
const FontSelection: React.FunctionComponent<FontSelectionProps> = ({
  title,
  value,
  setValue,
  previewText = "The quick brown fox jumps",
  fontWeight,
  setFontWeight,
  fontSize,
  setFontSize,
}) => {
  const classes = useStyles();
  const labelId = `font-select-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const weightId = `font-weight-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const weightLabelId = `${weightId}-label`;
  const sizeId = `font-size-${title.replace(/\s+/g, "-").toLowerCase()}`;

  const resolvedFontFamily = value ? `${value}, sans-serif` : "inherit";

  return (
    <div className={classes.wrapper}>
      <InputLabel id={labelId} className={classes.label}>
        {title}
      </InputLabel>
      <FormControl variant="outlined" size="small" className={classes.formControl}>
        <Select
          labelId={labelId}
          value={value ?? ""}
          onChange={(e: any) => setValue(e.target.value)}
        >
          {AVAILABLE_FONTS.map((font) => (
            <MenuItem key={font.label} value={font.value}>
              <span
                style={{
                  fontFamily: font.value ? `${font.value}, sans-serif` : "inherit",
                }}
              >
                {font.label}
              </span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className={classes.previewBox}>
        <Typography
          style={{
            fontFamily: resolvedFontFamily,
            fontWeight: fontWeight ? (fontWeight as any) : undefined,
            fontSize: isValidFontSize(fontSize)
              ? `calc(1em + ${fontSize}px)`
              : undefined,
          }}
          noWrap
        >
          {previewText}
        </Typography>
      </div>

      {/* The font weight is picked from the standard weights and is sent
          together with the font, separated by a star (for example
          "Poppins*600"). */}
      {setFontWeight && (
        <>
          <InputLabel id={weightLabelId} className={classes.label} style={{ marginTop: 10 }}>
            Font Weight
          </InputLabel>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <Select
              labelId={weightLabelId}
              id={weightId}
              value={fontWeight ?? ""}
              displayEmpty
              onChange={(e: any) => setFontWeight(e.target.value)}
            >
              <MenuItem value="">Default</MenuItem>
              {/* A weight saved before this list existed is still shown. */}
              {fontWeight && !FONT_WEIGHTS.includes(fontWeight) && (
                <MenuItem value={fontWeight}>{fontWeight}</MenuItem>
              )}
              {FONT_WEIGHTS.map((weight) => (
                <MenuItem key={weight} value={weight}>
                  {weight}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {/* The extra font size is typed in by hand and is sent after the font
          weight with a plus (for example "Poppins*600+2"). */}
      {setFontSize && (
        <>
          <InputLabel htmlFor={sizeId} className={classes.label} style={{ marginTop: 10 }}>
            Font Size
          </InputLabel>
          <TextField
            id={sizeId}
            type="number"
            inputProps={{ step: 1 }}
            variant="outlined"
            size="small"
            className={classes.formControl}
            value={fontSize ?? ""}
            onChange={(e: any) => setFontSize(cleanFontSize(e.target.value))}
          />
        </>
      )}
    </div>
  );
};

export default FontSelection;
